/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo, useContext, useEffect, useState } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { ApiContext } from '../../../../context/ApiContext';
import {
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD
} from '../../../shared/Utils/Constants';
import {
  IEmaasEnergyUsageComponentDataArea,
  IAny
} from '../../IEnergyManagementProps';
import {
  convertDataForBreakdownByMeter,
  convertDataForBreakdownByMeterWithIndividualScaling,
  convertUnitForPerformanceGraph,
  getPerformanceGraphData,
  getPerformanceGraphDataNew,
  getSpanDetails
} from '../../../shared/Utils/Utils';
import { getProgressBarUnitScaledData } from '../../../shared/Utils/portfolioDataFormatter';
import { ConversionType } from '../../../shared/Utils/unitHandling/UnitModel';
import {
  getNullCheckedAggregationEnergyCardData,
  getNullToZero
} from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import { getEnergyEndDate } from '../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import { IDataTableHeader } from '../../../shared/BreakdownByMeter/BreakdownByMeterProps';
import CommonEnergyComponent from '../../../shared/EnergyKPI/CommonEnergyComponent';
import { EnergyUsageField } from '../../../shared/Enums';
import { UnitUtility } from '@bmsp/bmsp.web.component.common';
import { getConvertedValue } from '../../../shared/Utils/unitHandling/unitHandling';
import _ from 'lodash';

interface IEmaasEnergyUsageComponentProps {
  data: IEmaasEnergyUsageComponentDataArea;
}

export const EmaasEnergyUsageComponent = (
  props: IEmaasEnergyUsageComponentProps
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { data } = props;
  const apiContext = useContext(ApiContext);
  const {
    onEnergyUsageViewDetailsClick,
    isBreakdownEquipment,
    userPreferedUnit
  } = apiContext;
  const onViewDetailsClick = () => {
    if (onEnergyUsageViewDetailsClick) {
      onEnergyUsageViewDetailsClick(
        _.toLower(sourceType) === 'water' ? 'water' : ''
      );
    }
  };

  let fromUnit: string;
  // if (true) {
  //   fromUnit = userPreferedUnit as any;
  // } else {
  //   fromUnit = energyMeasurementUnit;
  // }

  fromUnit = userPreferedUnit as any;

  const {
    summary: {
      totalConsumption,
      totalBaselineConsumption,
      totalEnergyDeviation,
      totalEnergyDeviationPercentage,
      absoluteTotalBaselineConsumption,
      absoluteTotalConsumption
    },
    performanceAggregation,
    aggregationsStartDate,
    aggregationsEndDate,
    performanceAggregationApplied,
    energyMeasurementUnit,
    areaWiseAggregations,
    breakdownByMeter,
    sourceType
  } = data;

  const SpanDetails = getSpanDetails(
    aggregationsStartDate,
    aggregationsEndDate
  );

  const consumptionBreakdownMeterData = convertDataForBreakdownByMeterWithIndividualScaling(
    isBreakdownEquipment ? breakdownByMeter : areaWiseAggregations,
    _.toLower(sourceType) === 'water' ? energyMeasurementUnit : fromUnit,
    [],
    energyMeasurementUnit,
    apiContext.showChangeWhenData,
    _.toLower(sourceType) === 'water'
      ? ConversionType.WATER
      : ConversionType.ELECTRICITY
  );
  const convertedPerformanceData = convertUnitForPerformanceGraph(
    performanceAggregation,
    _.toLower(sourceType) === 'water' ? energyMeasurementUnit : fromUnit,
    energyMeasurementUnit,
    _.toLower(sourceType) === 'water'
      ? ConversionType.WATER
      : ConversionType.ELECTRICITY
  );
  const { values } = consumptionBreakdownMeterData;

  const { displayUnit: performanceDisplayUnit } = convertedPerformanceData;

  const [usageType, setUsageType] = useState<string>(t(d.HIGHWITHOUTUNIT));

  const progressBarUnitScaledData = getProgressBarUnitScaledData(
    _.toLower(sourceType) === 'water'
      ? ConversionType.WATER
      : ConversionType.ELECTRICITY,
    { previousPeriod: totalBaselineConsumption, actual: totalConsumption },
    totalEnergyDeviation,
    _.toLower(sourceType) === 'water' ? energyMeasurementUnit : fromUnit, //energyMeasurementUnit,
    undefined,
    absoluteTotalBaselineConsumption,
    absoluteTotalConsumption,
    energyMeasurementUnit,
    apiContext.showChangeWhenData,
    true
  );

  const comparisonData =
    performanceAggregationApplied === 'Hourly'
      ? getPerformanceGraphData(
          performanceAggregation,
          energyMeasurementUnit,
          performanceDisplayUnit,
          performanceAggregationApplied,
          ConversionType.ELECTRICITY
        )
      : getPerformanceGraphDataNew(
          performanceAggregation,
          energyMeasurementUnit,
          performanceDisplayUnit,
          performanceAggregationApplied,
          ConversionType.ELECTRICITY,
          data.aggregationsStartDate,
          data.aggregationsEndDate
        );

  const isPercentageGreater =
    Math.abs(
      Number(
        getNullToZero(totalEnergyDeviationPercentage)?.toFixed(
          KPI_PERCENTAGE_DECIMAL_PRECISION
        )
      )
    ) > PERCENT_CHANGE_THRESHOLD;

  const aggregationEnergyData = {
    actual: progressBarUnitScaledData.actual,
    previousPeriod: progressBarUnitScaledData.previousPeriod,
    absoluteActual: progressBarUnitScaledData.absoluteActual,
    absolutePrevPeriod: progressBarUnitScaledData.absolutePreviousPeriod,
    actualUnit: progressBarUnitScaledData.actualUnit,
    baselineUnit: progressBarUnitScaledData.baselineUnit,
    changeUnit: progressBarUnitScaledData.changeUnit,
    savings:
      apiContext.showChangeWhenData &&
      progressBarUnitScaledData.deviation !== null &&
      isPercentageGreater
        ? null
        : progressBarUnitScaledData.deviation,
    savingsPercentage: parseFloat(
      getNullToZero(totalEnergyDeviationPercentage)?.toFixed(
        KPI_PERCENTAGE_DECIMAL_PRECISION
      )
    )
  };
  const headers = [];
  if (
    data?.addOn?.includes('NILM') &&
    props.data.subscriptionType === EnergyUsageField.PREMIUMSUBS
  ) {
    headers.push(
      {
        HeaderValue: t(d.BREAKDOWNBYAREAS),
        TextAlignValue: 'left',
        Field: 'type'
      },
      {
        HeaderValue: usageType,
        TextAlignValue: 'right',
        Field: 'high',
        sortable: true
      },
      {
        HeaderValue: t(d.CHANGE),
        TextAlignValue: 'right',
        Field: 'change',
        sortable: true
      }
    );
  } else {
    if (isBreakdownEquipment) {
      headers.push(
        {
          HeaderValue: t(d.BREAKDOWNBYEQUIPMENTTYPE),
          TextAlignValue: 'left',
          Field: 'type'
        },
        {
          HeaderValue: t(d.HIGHWITHOUTUNIT),
          TextAlignValue: 'right',
          Field: 'high',
          sortable: true
        }
      );
    } else {
      headers.push(
        {
          HeaderValue: t(d.BREAKDOWNBYAREAS),
          TextAlignValue: 'left',
          Field: 'type'
        },
        {
          HeaderValue: usageType,
          TextAlignValue: 'right',
          Field: 'high',
          sortable: true
        },
        {
          HeaderValue: t(d.CHANGE),
          TextAlignValue: 'right',
          Field: 'change',
          sortable: true
        }
      );
    }
  }

  const isNullCheckedEnergyCardData = getNullCheckedAggregationEnergyCardData(
    aggregationEnergyData,
    data.summary,
    apiContext.showChangeWhenData
  );
  const onSortingHandler = (sortOrder: number): void => {
    sortOrder === 1
      ? setUsageType(t(d.LOWWITHNOUNIT))
      : setUsageType(t(d.HIGHWITHOUTUNIT));
  };
  const energyComponentProps = {
    onViewDetailsClick: onViewDetailsClick,
    progressBarData: {
      aggregationData: isNullCheckedEnergyCardData,
      unit: progressBarUnitScaledData.ConvertedUnit,
      deviationScaledData: progressBarUnitScaledData.deviationScaledData
    },
    graphData: {
      chartSeries: comparisonData,
      startTime: aggregationsStartDate,
      endTime: aggregationsEndDate,
      aggregationType: performanceAggregationApplied
    },
    tableData: {
      columnHeaders: headers as IDataTableHeader[],
      meterData: values
    },
    dateData: SpanDetails,
    onSortingHandler: onSortingHandler,
    energyMeasurementUnit: energyMeasurementUnit,
    sourceType,
    fromCarbonKPI: true,
    originalUnit: energyMeasurementUnit,
    conversionType:
      _.toLower(sourceType) === 'water'
        ? ConversionType.WATER
        : ConversionType.ELECTRICITY
  };

  return <CommonEnergyComponent {...energyComponentProps} />;
};
export default memo(EmaasEnergyUsageComponent);
